import { useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box } from '@chakra-ui/react';
import DateFilter from '@inovua/reactdatagrid-enterprise/DateFilter';
import AssetManagerPage from 'components/common/AssetManagerPage';
import Layout from 'components/common/Layout';
import DataTable, { ActionsRenderer, booleanRenderer, dateRenderer, dateSort } from 'components/DataTable';
import { useBulkDeleteOrders, useDeleteOrder, useDownloadOrder, useFetchOrders, useTableData, useToast } from 'hooks';
import { checkIfError } from 'utils/helpers';
import { ThemeContext } from 'contexts/themeContext';
import { ActionLoadingCallback } from 'components/DataTable/Actions/Action';
import { ActionCallbackItem } from 'components/DataTable/Actions';
import { Pages } from 'constants/pages';
import { IOrderResource, IOrderCustomerData } from '../../../server/types';

interface IOrderWithCustomerData extends IOrderCustomerData, IOrderResource {
  isExpired: boolean;
}

type RenderProps = {
  data: IOrderWithCustomerData;
};

const defaultColumnInfo = {
  defaultFlex: 1,
  minWidth: 160,
};

const columns = [
  {
    header: 'Design',
    name: 'productName',
    ...defaultColumnInfo,
    minWidth: 100,
  },
  {
    header: 'Part Name',
    name: 'objectName',
    ...defaultColumnInfo,
  },
  {
    header: 'Date',
    name: 'createdAt',
    type: 'date',
    operator: 'inrange',
    dateFormat: 'YYYY/MM/DD',
    sort: dateSort,
    filterEditor: DateFilter,
    render: dateRenderer,
    ...defaultColumnInfo,
    minWidth: 100,
  },
  {
    header: 'Company Name',
    name: 'companyName',
    ...defaultColumnInfo,
    minWidth: 150,
  },
  {
    header: 'Customer First Name',
    name: 'customerFirstName',
    ...defaultColumnInfo,
  },
  {
    header: 'Customer Last Name',
    name: 'customerLastName',
    ...defaultColumnInfo,
  },
  {
    header: 'Customer Email',
    name: 'customerEmail',
    ...defaultColumnInfo,
  },
  {
    header: 'Customer Phone',
    name: 'customerPhone',
    ...defaultColumnInfo,
  },
  {
    header: 'Special Requests',
    name: 'specialRequests',
    ...defaultColumnInfo,
    minWidth: 150,
  },
  {
    header: 'ZIP/Postal Code',
    name: 'zipCode',
    ...defaultColumnInfo,
    minWidth: 150,
  },
  {
    name: 'isExpired',
    header: 'Expired',
    render: booleanRenderer,
    ...defaultColumnInfo,
    minWidth: 100,
  },
];

const OrderManager = () => {
  const [showToast] = useToast();
  const settings = useContext(ThemeContext);
  const [tableData, setTableData, { deleteItem, bulkDeleteItems }] = useTableData<IOrderResource>();
  const [fetchOrders, { error: fetchOrdersError, loading: fetchOrdersLoading }] = useFetchOrders();
  const [deleteOrder, { error: deleteError }] = useDeleteOrder();
  const [downloadOrder, { error: downloadError }] = useDownloadOrder();
  const [bulkDeleteOrders, { loading: bulkDeleteLoading }] = useBulkDeleteOrders();

  const isRFQ = settings?.rfqMode === 'true';

  const handleLoadOrders = async () => {
    const ordersResult = await fetchOrders();

    if (checkIfError(ordersResult)) return;

    setTableData(ordersResult);
  };

  useEffect(() => {
    const message = deleteError ?? downloadError ?? fetchOrdersError;

    if (!message) return;

    showToast({
      message,
      status: 'error',
    });
  }, [deleteError, downloadError, fetchOrdersError]);

  useEffect(() => {
    handleLoadOrders();
  }, []);

  const handleBulkDelete = async ({ ids }: ActionCallbackItem<IOrderResource>, cb: ActionLoadingCallback) => {
    if (!ids) return;

    await bulkDeleteOrders({ ids });
    bulkDeleteItems(ids, 'id');

    showToast({ message: `${ids.length} order(s) deleted successfully.` });

    cb();
  };

  const handleDownload = async (
    { objectName: resultFileName, bucketName }: IOrderResource,
    _: IOrderResource,
    cb: (isLoading: boolean) => void
  ) => {
    cb(true);

    const result = await downloadOrder({ bucketName, resultFileName });

    cb(false);

    window.location.href = result as string;
  };

  const handleDelete = async (
    itemData: IOrderWithCustomerData,
    _: IOrderWithCustomerData,
    cb: (isLoading: boolean) => void
  ) => {
    cb(true);

    if (!itemData.id) return;

    const deleteOrderResult = await deleteOrder(itemData.id);

    if (checkIfError(deleteOrderResult)) return;

    deleteItem(itemData.id, 'id');

    cb(false);

    showToast({
      message: `${!isRFQ ? 'Order' : 'RFQ'} deleted successfully.`,
    });
  };

  const inlineActions = [
    {
      type: 'delete',
      renderTitle: (data: IOrderWithCustomerData) =>
        `Delete${
          data.customerFirstName && data.customerLastName ? ` ${data.customerFirstName} ${data.customerLastName}'s` : ''
        } order?`,
      renderMessage: () => 'You are about to permanently delete this item, do you want to proceed?',
      callback: handleDelete,
    },
    {
      type: 'download',
      condition: ({ isExpired }: IOrderWithCustomerData) => !isExpired,
      callback: handleDownload,
    },
  ];

  const tableActions = [
    {
      type: 'filter',
      callback: () => {},
    },
    {
      type: 'bulkDelete',
      loading: bulkDeleteLoading,
      callback: handleBulkDelete,
    },
  ];

  return (
    <Layout testId="order-manager-page" pages={[Pages.OrderRfqManager]}>
      <Helmet title={!isRFQ ? 'Order Manager' : 'RFQ Manager'} />
      <AssetManagerPage title={!isRFQ ? 'Order Manager' : 'RFQ Manager'}>
        <Box mb={2}>
          <DataTable<IOrderResource>
            id="order-archive-8"
            rows={tableData?.map((tableItem) => ({ ...tableItem, ...tableItem.customerData })) ?? []}
            columns={[
              ...columns,
              {
                id: '$actions',
                header: 'Actions',
                render: (renderProps: RenderProps) => (
                  <ActionsRenderer<IOrderWithCustomerData> {...renderProps} actions={inlineActions} />
                ),
              },
            ]}
            exportParams={{
              model: 'Order',
              fields: ['createdAt', ...columns.map(({ name }) => name), 'customerData'],
              removeFields: ['created'],
            }}
            tableProps={{ idProperty: 'id' }}
            loading={fetchOrdersLoading}
            actions={tableActions}
          />
        </Box>
      </AssetManagerPage>
    </Layout>
  );
};

export default OrderManager;
